import React from 'react';
import { Button, CustomCheckbox } from '@hwm/ui-lib';
import { Menu, Typography } from '@material-ui/core';
import { Trans } from '@lingui/macro';
import { ArrowDropUp } from '@material-ui/icons';
import { SiteOrUserModel, SiteSummaryModel } from '@shared/models';
import { isEmpty } from 'lodash';

interface SitesSelectorProps {
  userSites: SiteSummaryModel[];
  selectedSites: SiteOrUserModel[];
  setSitesFilter: (payload: SiteOrUserModel[]) => void;
  showSiteInformationFilters: boolean;
}

export const SitesSelector: React.FC<SitesSelectorProps> = ({
  userSites,
  selectedSites,
  setSitesFilter,
  showSiteInformationFilters,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  return (
    <div data-testid="main-filters-sites-selector-wrapper">
      <Button
        style={{
          height: 36,
          width: 100,
          padding: 8,
          paddingRight: 4,
        }}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          setAnchorEl(event.currentTarget);
        }}
        disabled={!userSites || isEmpty(userSites)}
        dataTestId="main-filters-sites-selector-button"
      >
        <div className="flex w-full justify-between items-center">
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {showSiteInformationFilters ? <Trans>Site</Trans> : <Trans>Sites</Trans>}
          </Typography>
          <ArrowDropUp
            style={{
              fontSize: 30,
              transform: anchorEl ? '' : 'rotate(180deg)',
            }}
          />
        </div>
      </Button>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        getContentAnchorEl={null}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        classes={{ paper: 'px-4 pt-2' }}
        PaperProps={{
          style: { maxHeight: 260 },
        }}
        data-testid="main-filters-sites-selector-menu"
      >
        <div className="flex flex-col">
          {userSites.map((site) => (
            <CustomCheckbox
              key={site.id}
              label={site.siteID}
              checked={selectedSites.some(({ id }) => id === site.id)}
              onChange={(_, check) => {
                const newSites = showSiteInformationFilters ? [] : [...selectedSites];
                if (check) {
                  setSitesFilter([...newSites, { id: site.id, name: site.siteID }]);
                } else {
                  setSitesFilter(newSites.filter((s) => s.id !== site.id));
                }
              }}
              style={{ marginBottom: 4 }}
              newEditableLook
              dataTestId={`main-filters-sites-selector-checkbox-${site.id}`}
            />
          ))}
          {!showSiteInformationFilters ? (
            <Button
              style={{
                height: 28,
                marginTop: 8,
              }}
              variant="text"
              disabled={selectedSites.length === userSites.length}
              onClick={() =>
                selectedSites.length !== userSites.length
                  ? setSitesFilter(userSites?.map(({ id, siteID }) => ({ id, name: siteID })))
                  : null
              }
              dataTestId="main-filters-sites-selector-select-all-button"
            >
              <Trans>Select All</Trans>
            </Button>
          ) : null}
        </div>
      </Menu>
    </div>
  );
};
